import * as React from "react"
import { isArray } from "@apollo/client/utilities"
import {
  DirectionFromOrigin,
  type IntervalOrigin,
  type MonetaryValue,
  type TransactionFilter,
  useMinMaxAmountQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import monetaryValueHelper from "@digits-shared/helpers/monetaryValueHelper"
import numberHelper from "@digits-shared/helpers/numberHelper"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

const DEFAULT_MIN = 0
const DEFAULT_MAX = 10000

export function useAmountFilter({
  filters,
  origin,
  skip,
}: {
  filters: Omit<TransactionFilter, "viewKey">
  origin?: IntervalOrigin
  skip?: boolean
}) {
  const intervalOrigin = useIntervalOrigin()
  const viewKey = useViewVersion()

  const { data } = useMinMaxAmountQuery({
    variables: {
      origin: {
        ...(origin || intervalOrigin),
        intervalCount: origin?.intervalCount || intervalOrigin.intervalCount || 1,
      },
      direction: DirectionFromOrigin.Past,
      filter: {
        viewKey,
        ...filters,
      },
    },
    skip,
    notifyOnNetworkStatusChange: true,
  })

  return React.useMemo(() => {
    const min = data?.minMaxAmount?.minAmount
      ? monetaryValueHelper.unmultipliedAmount(data.minMaxAmount.minAmount)
      : DEFAULT_MIN

    const max = data?.minMaxAmount?.maxAmount
      ? monetaryValueHelper.unmultipliedAmount(data.minMaxAmount.maxAmount)
      : DEFAULT_MAX

    const rangeMin = Math.floor(min / 10) * 10
    const rangeMax = Math.ceil(max / 10) * 10

    const step = calculateStep(rangeMax - rangeMin)

    return {
      formattedMin: formatCurrency(data?.minMaxAmount?.minAmount),
      formattedMax: formatCurrency(data?.minMaxAmount?.maxAmount),
      min,
      max,
      rangeMin,
      rangeMax,
      step,
    }
  }, [data])
}

function formatCurrency(value: MonetaryValue | undefined) {
  const formattedValue = value ? numberHelper.currency(value) : ""
  return isArray(formattedValue) ? formattedValue[0] : formattedValue
}

function calculateStep(rangeDifference: number) {
  if (rangeDifference <= 100) return 1
  if (rangeDifference <= 1000) return 10
  if (rangeDifference <= 10000) return 100
  if (rangeDifference <= 100000) return 1000
  return 10000
}
